<template>
  <div class="mapping-container">
    <h3>tag settings</h3>
    <div class="error-message">{{pageError}}</div>

    <div class="mappings-wrapper">
      <div class="column divider-vertical">
        <div class="header">identify yourself</div>

        <div class="key-terms-wrapper" v-sb-click-outside="getSearchResults">
          <span class="key-terms-grid decrease-columns-to-mobile">
            <div v-for="(word, index) in keyTerms" :key="index" class="key-term">
              <p>{{word}}</p>
              <button class="remove-key-term" @click="removeKeyTerm(index)">x</button>
            </div>
            <div v-show="isAddingKeyTerm" class="key-term-input-wrapper">
              <input
                v-model="newSearchText"
                class="key-term-input"
                ref="keyTermInput"
                type="text"
                @keydown.enter="addKeyTerm">
              <div class="error-message">{{keyTermInputError}}</div>
            </div>
            <a v-show="!isAddingKeyTerm" class="add-key-term" @click="showKeyTermInput">+ key term</a>
          </span>
        </div>

        <div class="suggestions-wrapper">
          <div class="sub-header">suggested</div>
          <loading-icon v-if="suggestedLoading"></loading-icon>
          <suggested-tags
              v-else
              :suggestedError="suggestedError"
              @updateTag="updateTag"></suggested-tags>
        </div>

        <div class="divider"></div>
        <div class="sub-header retailers">retailers (select retailers you sell to, then identify your tags)</div>
        <search-bar
            v-if="retailersTags"
            :clearSearch="clearSearch"
            sectionType="retailers"
            isDebounce
            placeholderText="search retailers"
            @getSearchResult="getTags"></search-bar>
        <loading-icon v-if="retailersLoading"></loading-icon>
        <retailer-tags-widget
            v-else
            :retailersTags="retailersTags"
            :retailersError="retailersError"
            :advertiserId="currentAdvertiser.id"
            @showConfirmModal="showConfirmModal"
            @refreshAllSections="refreshAllSections"></retailer-tags-widget>
        <div class="divider"></div>
      </div>

      <div class="column">
        <div class="header selected">tags that are you</div>
        <search-bar
            v-if="linkedTags"
            :clearSearch="clearSearch"
            sectionType="linked"
            isDebounce
            placeholderText="search by retailer"
            @getSearchResult="getTags"></search-bar>
        <loading-icon v-if="linkedLoading"></loading-icon>
        <linked-tags
            v-else
            :currentPage="currentPage"
            :linkedTags="linkedTags"
            :linkedError="linkedError"
            :totalPageCount="totalPageCount"
            @changePage="changePage"
            @showConfirmModal="showConfirmModal"></linked-tags>
      </div>
    </div>

    <modal
        v-if="showModal"
        buttonText="confirm"
        showClose
        wrapperClasses="warning-modal"
        @close="showModal = false"
        @proceed="updateTag('delete', modalConfig.id)">

      <div slot="header" class="header">are you sure you want to remove this linked tag?</div>

      <div slot="body" class="body">
        <p class="tag-confirm">{{modalConfig.tag}}</p>
        <p class="retailer-confirm">{{modalConfig.retailer}}</p>
      </div>
    </modal>

  </div>
</template>

<script>
  import LinkedTags from './LinkedTags'
  import LoadingIcon from '../Shared/LoadingIcon'
  import Modal from '../Shared/Modal'
  import RetailerTagsWidget from './RetailerTagsWidget'
  import SearchBar from '../Shared/SearchBar'
  import SuggestedTags from './SuggestedTags'
  import { brandsAPI } from '../../services/api/brands'
  import { scrollToBottom, trimExtraWhiteSpaces } from '../../utilities'

  export default {
    props: {
      currentAdvertiser: Object,
    },

    name: 'Settings',

    components: {
      LinkedTags,
      LoadingIcon,
      Modal,
      RetailerTagsWidget,
      SearchBar,
      SuggestedTags,
    },

    data() {
      return {
        clearSearch: false,
        isAddingKeyTerm: false,
        isLoading: false,
        keyTerms: [],
        keyTermsBeforeAdding: [],
        keyTermInputError: null,
        linkedError: null,
        linkedLoading: false,
        linkedTags: null,
        modalConfig: {},
        newSearchText: "",
        pageError: null,
        retailersTags: null,
        retailersLoading: false,
        showModal: false,
        suggestedError: null,
        suggestedLoading: false,
        suggestedTags: null,
        retailersError: null,
        totalPageCount: null,
        currentPage: 1,
      }
    },

    methods: {
      addKeyTerm() {
        const trimmedText = trimExtraWhiteSpaces(this.newSearchText),
              filteredText = trimExtraWhiteSpaces(trimmedText?.replace(/[^a-zA-Z0-9 ']/g, '')),
              isMaxChar = filteredText?.length > 60

        this.keyTermInputError = isMaxChar ? 'max character 60' : null
        this.newSearchText = isMaxChar ? filteredText : null
        if (!isMaxChar && filteredText?.length > 0) this.keyTerms.push(filteredText)
      },

      changePage(page) {
        this.currentPage = page
        this.getTags('linked')
      },

      fetchSearchSuggestedTags() {
        this.suggestedLoading = true
        const keyTerms = this.keyTerms.join(', ')
        brandsAPI.updateAdvertiser(this.currentAdvertiser.id, {
          'key_terms': keyTerms
        }).then(async () => {
          await this.$store.dispatch('FETCH_SUGGESTED_TAGS', { advertiserID: this.currentAdvertiser.id })
        }).catch((e) => {
          this.$store.commit('SET_ERRORS', { errorKey: 'suggestedTags', errorValue: e })
        }).finally(() => {
          this.suggestedLoading = false
        })
      },

      getSearchResults() {
        this.addKeyTerm()
        if (this.keyTermInputError) return
        this.isAddingKeyTerm = false
        if (this.keyTermsBeforeAdding !== JSON.stringify(this.keyTerms)) {
          this.keyTermsBeforeAdding = JSON.stringify(this.keyTerms)
          this.fetchSearchSuggestedTags()
        }
      },

      getTags(type, searchText) {
        const view = this,
            sectionLoading = `${type}Loading`,
            search = `${type === 'linked' ? '&' : '?'}search=${searchText}`,
            baseUrl = view.getTagsUrl(type),
            url = `${baseUrl}${searchText ? search : ''}`

        view[sectionLoading] = true
        view.$api.get(url)
          .then((response) => {
            const data = response.data
            if (data) {
              view[`${type}Tags`] = data.pagination ? response.data.data : response.data
              response.data.pagination ? view.setPagination(data.pagination.page, data.pagination.total) : null
              view[`${type}Error`] = null
            } else {
              view[`${type}Error`] = 'there was an error loading, please refresh or contact support'
            }
            view[sectionLoading] = false
          })
          .catch((error) => {
            view[`${type}Error`] = `there was an issue: ${error}`
            view[sectionLoading] = false
          })
      },

      getTagsUrl(type) {
        const pagination = `?page=${this.currentPage}&per_page=20`,
            base = `/brands/${this.currentAdvertiser.id}`,
            linkedRouteEndpoint = `linked_tags${pagination}`,
            url = `${base}/${type === 'retailers' ? 'retailers' : linkedRouteEndpoint}`

        return url
      },

      refreshAllSections(sectionToSkip) {
        const sections = ['linked', 'retailers']
        sections.forEach(section => {
          if (section === sectionToSkip) { return }
          this.clearSearch = true
          this.getTags(section)
        })
      },

      removeKeyTerm(index) {
        this.isAddingKeyTerm = false
        this.keyTerms.splice(index, 1)
        this.fetchSearchSuggestedTags()
      },

      setPagination(page, totalPages) {
        this.currentPage = page
        this.totalPageCount = totalPages
      },

      showConfirmModal(id, tag, retailer) {
        this.modalConfig = {
          id: id,
          tag: tag,
          retailer: retailer
        }
        this.showModal = true
      },

      showKeyTermInput() {
        this.isAddingKeyTerm = true
        this.$nextTick(() => {
          this.$refs.keyTermInput.focus()
        })
      },

      updateTag(type, id, merchant_id) {
        const view = this,
            deleteType = type === 'delete',
            blacklistType = type === 'blacklist',
            url = `/brands/${view.currentAdvertiser.id}/linked_tags${deleteType ? '/' + id : ''}`,
            action = deleteType ? 'delete' : 'post',
            dataToUpdate = {
              tag_id: id,
              merchant_id: merchant_id,
              advertiser_id: view.currentAdvertiser.id,
              status: blacklistType ? 'blacklisted' : 'claimed'
            }
        this.suggestedLoading = true

        this.$api[action](url, deleteType ? null : dataToUpdate)
          .then(async (response) => {
            const data = response.data
            if (data) {
              await this.$store.dispatch('FETCH_SUGGESTED_TAGS', { advertiserID: this.currentAdvertiser.id })
              this.suggestedLoading = false
              this.refreshAllSections()
              deleteType ? view.showModal = false : null
            } else {
              view.pageError = 'there was an error updating tag, please refresh or contact support'
            }
            view.clearSearch = false
          })
          .catch((error) => {
            view.pageError = `there was an issue updating tag: ${error}`
            view.clearSearch = false
          })
      },
    },

    created() {
      this.refreshAllSections()
      const keyTerms = this.currentAdvertiser.key_terms,
            savedKeyTerms = keyTerms?.length > 0 ? keyTerms?.split(', ') : null
      savedKeyTerms && (this.keyTerms = [...savedKeyTerms])
      this.keyTermsBeforeAdding = JSON.stringify(this.keyTerms)
    },

    updated() {
      this.isAddingKeyTerm && scrollToBottom('.key-terms-grid')
    }
  }
</script>

<style lang="sass">
@import '../../assets/sass/colors'

.mapping-container
  color: $title-color
  padding-top: 2rem
  text-align: center

  h3
    margin-bottom: 2rem

.mappings-wrapper
  display: flex
  justify-content: space-between
  padding: 0 1rem
  @media only screen and (max-width: 64em)
    flex-direction: column
    justify-content: center

  .header
    font-weight: bold
    margin-bottom: 1.5rem

  .column
    flex-basis: 50%
    padding: 2rem
    @media only screen and (max-width: 64em)
      flex-basis: 100%
      padding: 0

  .column:first-child
    align-items: center
    display: flex
    flex-direction: column
    margin-right: 0
    @media only screen and (max-width: 64em)
      padding-bottom: 0

  .suggestions-wrapper
    min-height: 250px
    width: 100%

  .container
    width: 100%

  .button
    border: 1px solid $primary-color
    font-size: .9rem
    min-width: 60px
    padding: .075rem .15rem
    text-align: center

  .divider-vertical
    @media only screen and (max-width: 64em)
      border-right: none

  .search-bar-container
    width: 100%

.key-terms-wrapper
  border: 1px solid $primary-color
  border-radius: 17px
  margin-bottom: 10px
  padding: .5em 10px
  width: 80%
.key-terms-grid
  align-items: center
  gap: .5em
  grid-template-columns: repeat(4, 1fr)
  display: grid
  max-height: 120px
  overflow: auto
  width: 100%
  min-height: 0
  min-width: 0
  .add-key-term
    color: $primary-color
    cursor: pointer
    text-align: left
  .key-term
    align-items: center
    border-radius: 6px
    background-color: $primary-transparent
    display: flex
    min-width: 0
    p
      font-size: 14px
      margin: 4px auto 4px 6px
      overflow: hidden
      padding: 1px 0
      text-align: left
      text-overflow: ellipsis
      width: 80%
      white-space: nowrap
    .remove-key-term
      background: none
      border: none
      cursor: pointer
      font-family: 'AileronThin'
      outline: none
.key-term-input-wrapper
  min-width: 0
  .error-message
    font-size: small
    text-align: left
  .key-term-input
    border: 1px solid $title-color
    border-radius: unset
    font-size: .85em
    margin-bottom: 0
    outline: none
    padding: 1px 2px
    width: -webkit-fill-available

.warning-modal
  h3
    padding: 1rem 1rem 0 1rem

  p
    padding: .25rem

  .body
    margin-bottom: 1rem

  .tag-confirm
    font-weight: bold

  .retailer-confirm
    font-size: .9rem
</style>
